import {
  type CreateUserRequest,
  v1UsersCreate,
  v1UsersUsernameAvailabilityList,
} from '@on3/api';
import {
  emailSchema,
  passwordSchema,
  USERNAME_ERROR,
  usernameSchema,
} from '@on3/ui-lib/api/schema/validation/auth';
import { AppleButton } from '@on3/ui-lib/components/Auth/Buttons/Apple';
import { GoogleButton } from '@on3/ui-lib/components/Auth/Buttons/Google';
import { OpenPassButton } from '@on3/ui-lib/components/Auth/Buttons/OpenPass';
import { type FormProps } from '@on3/ui-lib/components/Auth/Forms';
import { Brands } from '@on3/ui-lib/components/Brands';
import { Form } from '@on3/ui-lib/components/Form';
import { Input } from '@on3/ui-lib/components/Input/Input';
import { Link } from '@on3/ui-lib/components/Link';
import { externalApi, useAuth, useSite } from '@on3/ui-lib/index';
import { useDynamicRoute } from '@on3/ui-lib/utils/hooks/useDynamicRoute';
import { trackGoogle } from '@on3/ui-lib/utils/tracking';
import { useCallback, useState } from 'react';
import { object } from 'yup';

import styles from './AuthForm.module.scss';

const schema = object({
  email: emailSchema.required('Email is required'),
  username: usernameSchema.required('Username is required'),
  password: passwordSchema.required('Password is required'),
});

export const RegisterForm = ({
  showHeader = true,
  switchForm,
  callback,
}: FormProps) => {
  const { currentSite } = useSite();
  const { formatHref } = useDynamicRoute();
  const { setUserToken } = useAuth();

  // Generate a username based on the site's mascot or name and append random numbers
  const generateUsername = useCallback(() => {
    const mascot = currentSite.organization?.mascot;
    const name =
      currentSite.organization?.knownAs || currentSite.organization?.name;

    const _prefix = (mascot || name || 'anon')
      .toLowerCase()
      .replace(/\s+/g, '_')
      .slice(0, 45);

    // mascot + random 6 numbers
    return 'Anon' + Math.floor(100000 + Math.random() * 900000).toString();
  }, [currentSite.organization]);

  const [state, setState] = useState<CreateUserRequest>({
    email: '',
    username: generateUsername(),
    password: '',
    process: true,
  });
  const [error, setError] = useState<string | null>(null);

  const checkUsernameAvailability = useCallback(async () => {
    if (
      !state.username ||
      state.username.length < 3 ||
      state.username.length > 20
    )
      return;

    setError(null);

    try {
      const { available } = await v1UsersUsernameAvailabilityList(externalApi, {
        username: state.username,
      });

      if (!available) {
        setError(USERNAME_ERROR.TAKEN);
      }
    } catch (e: any) {
      console.error('Username availability check error:', e);
    }
  }, [state.username]);

  const onSubmit = async (values: CreateUserRequest) => {
    setError(null);

    try {
      const user = await v1UsersCreate(externalApi, values);

      trackGoogle('sign_up', {
        email: values.email,
        username: values.username,
        siteKey: currentSite.key,
      });

      setUserToken(user);
      callback?.(user);
    } catch (e: any) {
      setError(e.response?.data || 'Registration failed. Please try again.');
      console.error('Registration error:', e.response?.data || e.message);
    }
  };

  return (
    <Form
      className={styles.block}
      onSubmit={onSubmit}
      schema={schema}
      values={state}
    >
      {({ errors, processing, validate }) => (
        <>
          {showHeader && (
            <header className={styles.header}>
              <h1>Register</h1>
              <Brands className={styles.brands} />
              <p>
                Gain access to both <strong>On3</strong> and{' '}
                <strong>Rivals</strong> Networks.
              </p>
            </header>
          )}
          <div className={styles.fields}>
            <div className={styles.field}>
              <Input
                autoComplete="email"
                error={errors.email}
                id="email"
                label="Email Address"
                name="email"
                onBlur={validate}
                onChange={(e) => setState({ ...state, email: e.target.value })}
                required
              />
            </div>
            <div className={styles.field}>
              <Input
                autoComplete="current-password"
                error={errors.password}
                id="password"
                label="Password"
                name="password"
                onBlur={validate}
                onChange={(e) =>
                  setState({ ...state, password: e.target.value })
                }
                required
                type="password"
              />
            </div>
            <div className={styles.field}>
              <Input
                autoComplete="username"
                error={errors.username}
                id="username"
                label="Username"
                name="username"
                onBlur={(e) => validate(e).then(checkUsernameAvailability)}
                onChange={(e) =>
                  setState({ ...state, username: e.target.value })
                }
                required
                value={state.username || ''}
              />
            </div>
          </div>
          {error && <p className={styles.error}>{error}</p>}
          <button
            className={styles.btnSubmit}
            disabled={processing}
            type="submit"
          >
            {processing ? 'Registering...' : 'Register'}
          </button>
          <p className={styles.or}>
            <span>or continue with</span>
          </p>
          <div className={styles.sso}>
            <AppleButton />
            <GoogleButton />
            <OpenPassButton />
          </div>
          <p className={styles.terms}>
            By clicking continue to sign up, you&apos;re agreeing to our{' '}
            <Link href="/page/terms-of-service/" target="_blank">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link href="/page/privacy-policy/" target="_blank">
              Privacy Policy
            </Link>{' '}
            and{' '}
            <Link href="/page/childrens-privacy-policy/" target="_blank">
              Children&apos;s Privacy Policy
            </Link>
          </p>
          <footer className={styles.footer}>
            {switchForm ? (
              <button onClick={() => switchForm('login')} type="button">
                Already have an account?
              </button>
            ) : (
              <Link
                href={formatHref({
                  path: '/[site]/login/',
                  params: { site: currentSite.url || '' },
                })}
              >
                Already have an account?
              </Link>
            )}
          </footer>
        </>
      )}
    </Form>
  );
};
